import { Button, Select, Text } from '@cabonline/ui-web';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useLocale } from '@multiplatform-apps/service-trips';
import { FC, useCallback, useMemo } from 'react';
import { SingleValue } from 'react-select';

import {
  StyledPaginatorContainer,
  StyledPaginatorItem,
  StyledPaginatorSelectWrapper,
} from './styles';

export const defaultPageSize = 10;
export const defaultPageNum = 1;
export const defaultPageGrouping = 'groupTrip';
export const validPageSizes = [10, 20, 50, 100] as const;

const itemsPerPageOptions = [
  {
    value: '10',
    label: '10',
  },
  {
    value: '20',
    label: '20',
  },
  {
    value: '50',
    label: '50',
  },
  {
    value: '100',
    label: '100',
  },
];

interface PaginatorProps {
  data?: unknown[];
  currentPage: number;
  setCurrentPage: (currentPage: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (itemsPerPage: number) => void;
  count?: number;
  pageSizeOptions?: ReadonlyArray<number>;
}

type SelectOption = { value: string; label: string };

const computeTotalPages = (
  count: number | undefined,
  data: unknown[] | undefined,
  itemsPerPage: number
): number => {
  if (count) return Math.ceil(count / itemsPerPage);
  if (data) return Math.ceil(data.length / itemsPerPage);
  return 0;
};

const computePageOptions = (totalPages: number): SelectOption[] =>
  [...Array(totalPages).keys()].map((i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  }));

const resolveLastItem = (
  isLastPage: boolean,
  count: number | undefined,
  data: unknown[] | undefined,
  indexOfLastItem: number
): number | undefined => {
  if (!isLastPage) return indexOfLastItem;
  return count ?? data?.length;
};

const usePaginator = ({
  data,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  count,
  pageSizeOptions,
}: PaginatorProps) => {
  const resolvedSizeOptions: SelectOption[] = useMemo(
    () => (pageSizeOptions ?? validPageSizes).map((n) => ({ value: String(n), label: String(n) })),
    [pageSizeOptions],
  );
  const totalPages = useMemo(
    () => computeTotalPages(count, data, itemsPerPage),
    [count, data, itemsPerPage]
  );

  const pageOptions = useMemo(() => computePageOptions(totalPages), [totalPages]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const isLastPage = totalPages === currentPage;

  const lastItem = resolveLastItem(isLastPage, count, data, indexOfLastItem);

  const handleItemsPerPageChange = useCallback((newValue: SingleValue<SelectOption>) => {
    setCurrentPage(1);
    setItemsPerPage(newValue ? Number(newValue.value) : 10);
  }, [setCurrentPage, setItemsPerPage]);

  const handlePageChange = useCallback((newPage: SingleValue<SelectOption>) => {
    setCurrentPage(newPage ? Number(newPage.value) : 1);
  }, [setCurrentPage]);

  const paginate = useCallback((pageNumber: number) => setCurrentPage(pageNumber), [setCurrentPage]);

  return {
    totalPages,
    pageOptions,
    resolvedSizeOptions,
    indexOfFirstItem,
    isLastPage,
    lastItem,
    handleItemsPerPageChange,
    handlePageChange,
    paginate,
  };
};

export const Paginator: FC<PaginatorProps> = (props) => {
  const { t } = useLocale();
  const {
    totalPages,
    pageOptions,
    resolvedSizeOptions,
    indexOfFirstItem,
    isLastPage,
    lastItem,
    handleItemsPerPageChange,
    handlePageChange,
    paginate,
  } = usePaginator(props);

  const { currentPage, itemsPerPage, data, count } = props;

  return (
    <StyledPaginatorContainer>
      <StyledPaginatorItem>
        <Text>{t('paginator.itemsPerPageLabel')}</Text>
        <Select
          id="paginator-items-per-page"
          options={resolvedSizeOptions}
          value={{
            value: itemsPerPage.toString(),
            label: itemsPerPage.toString(),
          }}
          onChange={handleItemsPerPageChange}
          menuPlacement="top"
          aria-label={t('paginator.itemsPerPageLabel')}
        />
        <Text>
          {t('paginator.itemsLabel', {
            firstItem: indexOfFirstItem + 1,
            lastItem,
            count: count || data?.length,
          })}
        </Text>
      </StyledPaginatorItem>
      <StyledPaginatorItem alignRight>
        <StyledPaginatorSelectWrapper>
          <Select
            id="paginator-current-page"
            options={pageOptions}
            value={{
              value: currentPage.toString(),
              label: currentPage.toString(),
            }}
            onChange={handlePageChange}
            menuPlacement="top"
            aria-label={t('paginator.currentPage.ariaLabel')}
          />
          <Text>{t('paginator.totalPagesLabel', { totalPages })}</Text>
        </StyledPaginatorSelectWrapper>
        <Button
          iconLeft={<ChevronLeft />}
          variant="tertiary"
          disabled={currentPage === 1}
          onClick={() => paginate(currentPage - 1)}
          aria-label={t('paginator.previousButton.ariaLabel')}
        />
        <Button
          iconLeft={<ChevronRight />}
          variant="tertiary"
          onClick={() => paginate(currentPage + 1)}
          disabled={isLastPage}
          aria-label={t('paginator.nextButton.ariaLabel')}
        />
      </StyledPaginatorItem>
    </StyledPaginatorContainer>
  );
};
